import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sun, Moon, Sparkles, LogOut, Menu, Bell, MessageSquare, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Avatar, PriorityBadge } from "@/components/common/ui";
import { cn } from "@/lib/utils";

export default function Topbar({ onOpenCopilot, onOpenSidebar, onOpenPalette }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openMenu, setOpenMenu] = useState(null); // 'search' | 'notif' | 'msg' | null
  const [notifs, setNotifs] = useState([]);
  const [msgs, setMsgs] = useState({ threads: [], unread: 0 });
  const rootRef = useRef(null);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  const loadMsgs = () => api.get("/messages/recent").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => {
    loadNotifs(); loadMsgs();
    // Live polling — keep the bell & messages fresh without a manual refresh.
    const id = setInterval(() => { loadNotifs(); loadMsgs(); }, 20000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data.results || []); setOpenMenu("search"); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpenMenu(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const unreadNotif = notifs.filter((n) => !n.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };
  const openThread = async (t) => { await api.post(`/messages/${t.thread_id}/read`); setOpenMenu(null); loadMsgs(); navigate("/families"); };

  return (
    <header ref={rootRef} className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/85 px-4 backdrop-blur-xl sm:px-6">
      <button data-testid="open-sidebar-btn" onClick={onOpenSidebar} className="lg:hidden text-muted-foreground"><Menu size={20} /></button>

      <div className="relative hidden max-w-md flex-1 sm:block">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => q.length >= 2 && setOpenMenu("search")}
          placeholder="Search residents, staff, medications, compliance…"
          className="h-9 w-full rounded-md border bg-card pl-9 pr-16 text-sm outline-none transition-colors focus:border-[#155EEF] focus:ring-2 focus:ring-[#155EEF]/20" />
        <button data-testid="command-palette-trigger" onClick={onOpenPalette} title="Command palette (⌘K)"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-[#155EEF] hover:text-[#155EEF]">⌘K</button>
        {openMenu === "search" && results.length > 0 && (
          <div className="careiq-scroll absolute mt-2 max-h-80 w-full overflow-y-auto rounded-lg border bg-popover p-1.5 shadow-xl">
            {results.map((r, i) => (
              <button key={i} data-testid={`search-result-${i}`} onClick={() => { navigate(r.link); setOpenMenu(null); setQ(""); }}
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted">
                <span><span className="font-medium">{r.title}</span><span className="ml-2 text-xs text-muted-foreground">{r.subtitle}</span></span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">{r.type}</span></button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 sm:hidden" />

      <button data-testid="copilot-trigger-btn" onClick={onOpenCopilot}
        className="flex items-center gap-2 rounded-md bg-[#7C3AED] px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-[#7C3AED]/25 transition-colors hover:bg-[#6D28D9]">
        <Sparkles size={16} /> <span className="hidden sm:inline">AI Copilot</span>
      </button>

      {/* Notification bell */}
      <div className="relative">
        <button data-testid="notification-bell-btn" onClick={() => { setOpenMenu(openMenu === "notif" ? null : "notif"); loadNotifs(); }}
          className="relative flex h-9 w-9 items-center justify-center rounded-md border bg-card text-muted-foreground transition-colors hover:text-foreground">
          <Bell size={17} />
          {unreadNotif > 0 && <span data-testid="notification-badge" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F04438] px-1 text-[10px] font-bold text-white">{unreadNotif}</span>}
        </button>
        {openMenu === "notif" && (
          <div data-testid="notification-dropdown" className="absolute right-0 mt-2 w-80 rounded-lg border bg-popover shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-2.5"><span className="text-sm font-bold">Notifications</span>
              {unreadNotif > 0 && <button data-testid="mark-all-read-btn" onClick={markAll} className="flex items-center gap-1 text-xs font-medium text-[#155EEF] hover:underline"><CheckCheck size={13} /> Mark all read</button>}</div>
            <div className="careiq-scroll max-h-96 divide-y overflow-y-auto">
              {notifs.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No notifications</div>}
              {notifs.map((n) => (
                <div key={n.id} data-testid={`notif-${n.id}`} className={cn("flex items-start gap-2.5 px-4 py-3", !n.read && "bg-[#155EEF]/5")}>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#155EEF]" />}
                  <div className={cn("min-w-0 flex-1", n.read && "pl-4")}>
                    <div className="mb-0.5"><PriorityBadge level={n.priority} /></div>
                    <div className="text-[13px] leading-snug">{n.title}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { setOpenMenu(null); navigate("/alerts"); }} className="w-full border-t px-4 py-2.5 text-center text-xs font-medium text-[#155EEF] hover:bg-muted">View all alerts</button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="relative">
        <button data-testid="messages-btn" onClick={() => { setOpenMenu(openMenu === "msg" ? null : "msg"); loadMsgs(); }}
          className="relative flex h-9 w-9 items-center justify-center rounded-md border bg-card text-muted-foreground transition-colors hover:text-foreground">
          <MessageSquare size={17} />
          {msgs.unread > 0 && <span data-testid="messages-badge" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#7C3AED] px-1 text-[10px] font-bold text-white">{msgs.unread}</span>}
        </button>
        {openMenu === "msg" && (
          <div data-testid="messages-dropdown" className="absolute right-0 mt-2 w-80 rounded-lg border bg-popover shadow-xl">
            <div className="border-b px-4 py-2.5 text-sm font-bold">Family Messages</div>
            <div className="careiq-scroll max-h-96 divide-y overflow-y-auto">
              {msgs.threads.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No messages</div>}
              {msgs.threads.slice(0, 8).map((t) => (
                <button key={t.thread_id} data-testid={`msg-${t.thread_id}`} onClick={() => openThread(t)}
                  className={cn("flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-muted/50", t.unread > 0 && "bg-[#7C3AED]/5")}>
                  <Avatar name={t.guardian} seed={t.resident_name.length} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between"><span className="truncate text-[13px] font-semibold">{t.guardian}</span>{t.unread > 0 && <span className="ml-1 h-2 w-2 rounded-full bg-[#7C3AED]" />}</div>
                    <div className="truncate text-xs text-muted-foreground">re: {t.resident_name}</div>
                    <div className="truncate text-xs text-muted-foreground">{t.last_sender}: {t.preview}</div>
                  </div>
                </button>
              ))}
            </div>
            <button onClick={() => { setOpenMenu(null); navigate("/families"); }} className="w-full border-t px-4 py-2.5 text-center text-xs font-medium text-[#7C3AED] hover:bg-muted">Open Families</button>
          </div>
        )}
      </div>

      <button data-testid="theme-toggle-btn" onClick={toggle} className="flex h-9 w-9 items-center justify-center rounded-md border bg-card text-muted-foreground transition-colors hover:text-foreground">
        {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
      </button>

      <div className="flex items-center gap-2.5 border-l pl-3">
        <Avatar name={user?.name} size={34} />
        <div className="hidden leading-tight md:block"><div className="text-sm font-semibold">{user?.name}</div><div className="text-[11px] capitalize text-muted-foreground">{(user?.role || "").replace("_", " ")}</div></div>
        <button data-testid="logout-btn" onClick={logout} className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-[#F04438]"><LogOut size={17} /></button>
      </div>
    </header>
  );
}
